/**
 * Ion Command Layer
 *
 * Canonical command functions for the Explorer (filters/view/context). A mouse
 * click and an Ion tool call both go through the SAME functions here — neither
 * writes to filterStore/globalSignals/appViewMode directly. That gives every
 * state change a single choke point for provenance tracking (commandProvenance)
 * and lets a whole Ion turn be undone via begin/commit/revert transactions.
 */

import { signal, effect } from '@preact/signals'
import { navigate } from 'wouter-preact/use-browser-location'
import {
  basis,
  domain,
  sortBy,
  sortOrder,
  tableSortBy,
  tableSortOrder,
  sidebarVisible,
  freePlotXAxis,
  freePlotYAxis,
  freePlotColorBy,
  highlightedCellIds,
  tableDensity,
  tableVisibleColumns,
} from './globalSignals.js'
import { columnsForDensity } from '../utils/tableColumns.js'
import {
  toggleFilter,
  updateRange,
  resetAllFilters,
  pendingRangeOverrides,
  currentView,
  searchQuery,
  selectedChemistries,
  selectedCathodeChemistries,
  selectedAnodeChemistries,
  selectedManufacturers,
  selectedCountries,
  selectedFormFactors,
  selectedTechnologies,
  energyRange,
  powerRange,
  capacityRange,
  currentRange,
  cyclesRange,
  yearRange,
  batchVariationRange,
  agingRange,
  fullRangeEnergy,
  fullRangePower,
  fullRangeCapacity,
  fullRangeCurrent,
  fullRangeYear,
  fullRangeBatchVariation,
  fullRangeAging,
  dynamicFilterCounts,
  ionCellIdSet,
  ionCellIdSetLabel,
  clearIonCellSet,
  routeChemistryValues,
  cellIdsForChemistries,
} from './filterStore.js'
import { appViewMode } from './appViewMode.js'
import { activePreset, plotMode } from './plotPresets.js'
import { markIonBasisReloadExpected, isIonBasisReloadExpected } from './ionReloadGuard.js'

// Ion speaks about a single "chemistry" facet, but the live explorer filter
// is driven by cathode chemistry — selectedChemistries is legacy, used only
// for the old combined 'Analysis Ongoing' facet (filterStore.js:23/24).
const FACET_TYPE_MAP = {
  chemistry: 'cathode-chemistry',
}

// Categorical facet arrays, keyed by the same type strings toggleFilter()
// switches on. Also the snapshot/revert scope for facets.
const FACET_SIGNALS = {
  chemistry: selectedChemistries, // legacy
  'cathode-chemistry': selectedCathodeChemistries,
  'anode-chemistry': selectedAnodeChemistries,
  manufacturer: selectedManufacturers,
  country: selectedCountries,
  'form-factor': selectedFormFactors,
  technology: selectedTechnologies,
}

// Maps each categorical facet `type` to the dynamicFilterCounts key whose Map
// holds that facet's LIVE catalogue option strings (the exact values the
// grid's exact-match filter compares against). Used by resolveFacetValues to
// turn Ion's loose value ("NMC") into the real option(s) ("NMC (high-nickel)",
// "NMC (low-nickel)"). Legacy combined 'chemistry' has no counts key → not
// resolvable, passed through untouched.
const FACET_OPTIONS_KEY = {
  'cathode-chemistry': 'cathodeChemistries',
  'anode-chemistry': 'anodeChemistries',
  manufacturer: 'manufacturers',
  country: 'countries',
  'form-factor': 'formFactors',
  technology: 'technologies',
}

/**
 * Resolve Ion's loosely-phrased facet VALUES against the live catalogue
 * options for that facet.
 *
 * Ion emits human values ("NMC", "Samsung"); the grid filters by EXACT
 * option string ("NMC (high-nickel)" / "NMC (low-nickel)", "Samsung SDI").
 * An unmatched value silently collapsed the result to 0 cells (staging
 * 2026-07-23: "NMC 21700" → 0 despite 31 NMC 21700 cells, because the
 * catalogue splits NMC into high/low-nickel and neither equals "NMC").
 *
 * Resolution per requested value, against the live option set:
 *   1. exact match (case-insensitive)              → that option
 *   2. else options CONTAINING the value           → "NMC" → both NMC subtypes
 *   3. else options CONTAINED IN the value          → "NMC high nickel" → "NMC (high-nickel)"-ish
 *   4. no catalogue match                           → keep raw (honest 0, unchanged behaviour)
 *
 * Mirrors what a user does by hand: typing "NMC" and ticking every NMC row.
 * If the catalogue hasn't loaded yet (no counts), values pass through
 * unchanged so we never drop a filter just because metadata is late.
 */
function resolveFacetValues(type, values) {
  const countsKey = FACET_OPTIONS_KEY[type]
  if (!countsKey) return values

  const counts = dynamicFilterCounts.value?.[countsKey]
  if (!counts || counts.size === 0) return values

  const options = [...counts.keys()]
  const out = []
  const seen = new Set()

  for (const raw of values) {
    const want = String(raw).trim()
    if (!want) continue
    const wantLc = want.toLowerCase()

    let matches = options.filter(o => o.toLowerCase() === wantLc)
    if (matches.length === 0) {
      matches = options.filter(o => o.toLowerCase().includes(wantLc))
    }
    if (matches.length === 0) {
      matches = options.filter(o => wantLc.includes(o.toLowerCase()))
    }

    const resolved = matches.length ? matches : [want]
    for (const r of resolved) {
      if (!seen.has(r)) {
        seen.add(r)
        out.push(r)
      }
    }
  }

  return out
}

// Range signals, keyed by the same type strings updateRange() switches on.
// Also the snapshot/revert scope for ranges.
const RANGE_SIGNALS = {
  energy: energyRange,
  power: powerRange,
  capacity: capacityRange,
  current: currentRange,
  cycles: cyclesRange,
  year: yearRange,
  batchVariation: batchVariationRange,
  aging: agingRange,
}

// Full-range signal for each RANGE_SIGNALS key (except cycles — see
// CYCLES_DEFAULT_RANGE below), used to default a missing bound in
// applyFilterCommand's ranges loop (C1 fix).
const FULL_RANGE_SIGNALS = {
  energy: fullRangeEnergy,
  power: fullRangePower,
  capacity: fullRangeCapacity,
  current: fullRangeCurrent,
  year: fullRangeYear,
  batchVariation: fullRangeBatchVariation,
  aging: fullRangeAging,
}

// fullRangeCycles (filterStore.js) is never populated by
// initFilterRangesFromMetadata — there's no metadata.cycles branch — so it
// stays stuck at its stale {min:0, max:50000} default forever. Falling back
// to it here would silently widen a missing bound to that stale range
// instead of cyclesRange's actual reset default (filterStore.js:461).
const CYCLES_DEFAULT_RANGE = { min: 100, max: 5000 }

/**
 * Build the query string for navigating to `view`, MERGING into whatever
 * other params are currently in the URL instead of replacing the whole
 * query — cell detail renders inside /explorer via `?cell=&section=`
 * (MainAppPage.jsx:762), so a plain `?view=...` replace was throwing the
 * user out of an open detail view (I2). Pass `view` as null/undefined to
 * remove the param instead (used by restoreSnapshot when the pre-transaction
 * URL had none).
 */
function mergeViewIntoQuery(view) {
  const params = new URLSearchParams(window.location.search)
  if (view) {
    params.set('view', view)
  } else {
    params.delete('view')
  }
  const qs = params.toString()
  return qs ? `?${qs}` : window.location.pathname
}

// Provenance of the last command applied through this layer — lets the UI
// (and Task 10's click-redirect work) tell an Ion-driven change from a
// direct signal write that bypassed the command layer entirely.
export const commandProvenance = signal(null)

// M2 fix: two commands landing in the SAME millisecond (e.g. a synchronous
// set_filters + set_view in one real Ion turn) used to be indistinguishable
// by `at` alone — IonActionEcho.jsx's accumulateEcho deduped on
// `provenance.at <= lastHandledAt`, so the second write was silently
// dropped and its echo never fired. `seq` is a strictly-increasing
// module-level counter, unrelated to wall-clock time, so two same-ms writes
// still compare as distinct. `at` itself is untouched — Phase-1
// attribution/revert reads it.
let _provSeq = 0

/**
 * Write commandProvenance with a fresh `at`/`seq` pair. The single choke
 * point for every provenance write in this file (filter/view/context/
 * revert/commit) — see `_provSeq` above for why `seq` exists alongside `at`.
 * @param {string} command
 * @param {string} source
 * @param {object} [extra] - additional fields to merge in (e.g. commit's `label`)
 */
function setProvenance(command, source, extra = {}) {
  commandProvenance.value = { command, source, at: Date.now(), seq: ++_provSeq, ...extra }
}

// Depth counter, incremented for the duration of every apply*Command /
// restoreSnapshot call (including nested ones, e.g. applyFilterCommand's
// basis switch calling applyContextCommand) — > 0 while THIS layer is the
// one writing to the tracked signals below. The provenance-degradation
// effect uses it to tell "Ion/command-layer wrote this" from "something
// else wrote this" (a manual click that still calls toggleFilter() /
// updateRange() / etc. directly — Task 10).
let commandDepth = 0

/**
 * Apply a filter change: facet toggles + range updates.
 *
 * @param {object} opts
 * @param {boolean} [opts.merge=true] - false resets all filters before applying
 * @param {object} [opts.facets] - { chemistry: ['LFP'], manufacturer: [...], ... }
 * @param {object} [opts.ranges] - { energy: {min,max,basis?}, power: {...}, ... }
 * @param {'user'|'ion'} [opts.source='ion']
 */
export function applyFilterCommand({
  merge = true,
  facets = null,
  ranges = null,
  cellIds = null,
  cellIdsLabel = null,
  source = 'ion',
} = {}) {
  commandDepth++
  try {
    if (!merge) {
      resetAllFilters()
    }

    if (facets) {
      for (const [key, rawValues] of Object.entries(facets)) {
        const requested = Array.isArray(rawValues) ? rawValues : [rawValues]

        // `chemistry` needs routing, not a fixed mapping. FACET_TYPE_MAP sent
        // every value to cathode-chemistry, which is right for LFP/NMC/NCA and
        // wrong for the anode-only ones: "LFP and LTO" put both in the cathode
        // list, where LTO matches nothing — 29 cells shown for a request naming
        // 32, with a chip claiming otherwise. Reported by BA after the same bug
        // had already been fixed in the URL reader; this is the second caller
        // that had to agree, and `routeChemistryValues` is now the one
        // implementation both use.
        if (key === 'chemistry') {
          const routed = routeChemistryValues(requested)
          if (!routed.ready) {
            // Catalogue not loaded, so cathode-vs-anode is unknowable. Fall
            // through to the old behaviour rather than dropping the command:
            // being wrong about which facet beats ignoring the user.
            const values = resolveFacetValues('cathode-chemistry', requested)
            values.forEach((value) => {
              if (!selectedCathodeChemistries.value.includes(value)) {
                toggleFilter('cathode-chemistry', value)
              }
            })
            continue
          }
          if (routed.spansBoth) {
            // A union the facet panel cannot express (cathode AND anode). Apply
            // it as the cell SET, the primitive built for exactly that, so the
            // result is the cells asked for and carries a label explaining why
            // the Explorer is showing only those.
            const ids = cellIdsForChemistries(requested)
            if (ids.length) {
              ionCellIdSet.value = new Set(ids)
              ionCellIdSetLabel.value = requested.join(' & ')
            }
            continue
          }
          const target = routed.cathode.length ? 'cathode-chemistry' : 'anode-chemistry'
          const placed = routed.cathode.length ? routed.cathode : routed.anode
          const sig = FACET_SIGNALS[target]
          placed.forEach((value) => {
            if (!sig.value.includes(value)) toggleFilter(target, value)
          })
          continue
        }

        const type = FACET_TYPE_MAP[key] || key
        const sig = FACET_SIGNALS[type]
        if (!sig) continue

        // Resolve Ion's loose values ("NMC") to real catalogue options
        // ("NMC (high-nickel)"/"NMC (low-nickel)") before toggling, or the
        // exact-match grid filter drops them to 0 cells.
        const values = resolveFacetValues(type, requested)
        values.forEach(value => {
          // toggleFilter() is a pure toggle — only call it for values that
          // aren't selected yet, or it would remove them again.
          if (!sig.value.includes(value)) {
            toggleFilter(type, value)
          }
        })
      }
    }

    if (ranges) {
      for (const [type, range] of Object.entries(ranges)) {
        const { basis: rangeBasis, ...bounds } = range

        // Captured BEFORE the nested applyContextCommand call below can
        // mutate basis.value out from under this check.
        const switchesBasisNow = Boolean(rangeBasis && rangeBasis !== basis.value)
        if (switchesBasisNow) {
          applyContextCommand({ basis: rangeBasis, source })
        }

        // R4 fix: defer via pendingRangeOverrides not only when THIS range
        // carries a `basis` that itself just switched (switchesBasisNow —
        // pre-existing behavior), but also when the range carries NO basis
        // annotation at all AND an EARLIER command in the same Ion turn
        // already armed a reload that hasn't landed yet
        // (isIonBasisReloadExpected() — state/ionReloadGuard.js). Reproduces
        // as: ion-intro's intro-drive applies {basis:'grav'} first, then
        // ~1.4s later a PLAIN energy range with no `basis` of its own — from
        // THIS call's perspective no basis switch is happening, so the old
        // `switchesBasisNow`-only check missed it and let a still-pending
        // metadata reload (FilterPanel.jsx:651-683) land afterwards and
        // clobber the range with a plain updateRange() call. A range that
        // DOES carry an explicit basis already matching the current one
        // (the backend's normal set_filters payload — every range names the
        // basis it was computed against) is a caller-confirmed "we're on
        // this basis now" and always applies directly, same as before —
        // this only widens the deferred path for the basis-less case.
        const deferToReload = switchesBasisNow || (rangeBasis == null && isIonBasisReloadExpected())

        if (deferToReload) {
          // Route the RAW bounds through pendingRangeOverrides, with NO
          // defaulting from the current full range here — that range is the
          // PRE-reload one and may already be stale (the whole point of this
          // branch is that a reload is in flight). Leaving a missing bound
          // out of the override lets filterStore.js's
          // initFilterRangesFromMetadata fill it in from the FRESH full
          // range once the reload actually lands (filterStore.js:1452-1477),
          // instead of baking in a stale value that would silently win over
          // the fresh one.
          const overrides = { ...(pendingRangeOverrides.value || {}) }
          if (bounds.min != null) overrides[`${type}Min`] = bounds.min
          if (bounds.max != null) overrides[`${type}Max`] = bounds.max
          pendingRangeOverrides.value = overrides
        } else {
          // No reload in flight — the current full range is accurate, so
          // default any missing bound from it now. The backend serializes
          // ranges with exclude_none=True (explorer_commands.py) — a
          // one-sided range like "energy over 250" arrives as {min:250} with
          // NO `max` key at all, so `bounds.max` is `undefined` here (C1 fix
          // — was collapsing the result to 0 cells and showing a "250-NaN"
          // chip).
          const fullRange = FULL_RANGE_SIGNALS[type]?.value ?? CYCLES_DEFAULT_RANGE
          const min = bounds.min ?? fullRange.min
          const max = bounds.max ?? fullRange.max
          updateRange(type, { min, max })
        }
      }
    }

    // Explicit id set (D4). Part of set_filters rather than its own command
    // because it IS a filter on the cell list — same shape as a facet, and
    // merge=false has to clear it along with everything else, which
    // resetAllFilters() above already does.
    //
    // An EMPTY array clears the restriction instead of blanking the Explorer.
    // The backend rejects an empty list, so this only guards a hand-built call —
    // but "Ion sent nothing" resolving to "show the user nothing" is the wrong
    // way round for a mistake to fall.
    if (cellIds) {
      if (cellIds.length === 0) {
        clearIonCellSet()
      } else {
        ionCellIdSet.value = new Set(cellIds)
        ionCellIdSetLabel.value = cellIdsLabel || ''
      }
    }

    setProvenance('filter', source)
  } finally {
    commandDepth--
  }
}

/**
 * Apply a view change: grid/graph/table + sort.
 *
 * @param {object} opts
 * @param {'grid'|'graph'|'table'} [opts.view]
 * @param {string} [opts.sortBy]
 * @param {'asc'|'desc'} [opts.sortOrder]
 * @param {'user'|'ion'} [opts.source='ion']
 */
export function applyViewCommand({ view, sortBy: newSortBy, sortOrder: newSortOrder, source = 'ion' } = {}) {
  commandDepth++
  try {
    if (view) {
      const normalizedView = view === 'graph' ? 'ragone' : view
      appViewMode.value = `cells-${normalizedView}`
      currentView.value = normalizedView
      // URL keeps the un-normalized value — loadFiltersFromURL() expects
      // 'grid'|'graph'|'table' (filterStore.js:967), not 'ragone'. Merge into
      // the existing query (I2) instead of replacing it wholesale.
      navigate(mergeViewIntoQuery(view))
    }

    // Sort is stored PER VIEW: grid reads sortBy/sortOrder, table reads
    // tableSortBy/tableSortOrder (globalSignals.js) — two independent pairs so
    // a user can hand-sort each view differently. Ion's sort is an explicit
    // cross-view intent ("sort by energy density"), not a per-view tweak, so
    // write BOTH pairs: whichever view the user is in now — or switches to
    // next — reflects the sort the command asked for (fixes §10.2: an Ion sort
    // was invisible in the table because it only touched the grid pair).
    // Manual header/dropdown clicks still write only their own view's pair.
    if (newSortBy) {
      sortBy.value = newSortBy
      tableSortBy.value = newSortBy
    }
    if (newSortOrder) {
      sortOrder.value = newSortOrder
      tableSortOrder.value = newSortOrder
    }

    setProvenance('view', source)
  } finally {
    commandDepth--
  }
}

/**
 * Free-plot axes + colour-by (Ion's `set_plot`, C2).
 *
 * Separate from applyViewCommand because the two have different scopes: sort and
 * layout apply to every view, axes and colour only mean anything in the graph
 * view. It DOES switch the layout, though — the backend command always carries
 * `view: 'graph'`, because axes are invisible anywhere else and a set_plot that
 * left the user in grid would be a silent no-op. That is not the
 * "don't change layout as a side effect" case set_view warns about: there the
 * layout change would be incidental to a sort, here the plot is the request.
 *
 * Reuses applyViewCommand for the layout switch rather than repeating the
 * appViewMode/currentView/URL juggling — that logic has an easily-missed detail
 * (the 'graph' → 'ragone' normalisation, and merging into the existing query
 * instead of replacing it) which must not be duplicated.
 *
 * Runs inside `commandDepth` like every other applier, so a whole Ion turn stays
 * one transaction and REVERT restores the axes too (see takeSnapshot).
 */
export function applyPlotCommand({
  xAxis,
  yAxis,
  colorBy,
  highlightCellIds,
  view,
  source = 'ion',
} = {}) {
  commandDepth++
  try {
    // Layout first, so the chart the axes belong to is mounted before they
    // change — avoids a frame where the graph view renders with the old axes.
    if (view) applyViewCommand({ view, source })

    if (xAxis) freePlotXAxis.value = xAxis
    if (yAxis) freePlotYAxis.value = yAxis
    if (colorBy) freePlotColorBy.value = colorBy
    // Marked cells. An EMPTY array clears the marking rather than marking
    // nothing — same reasoning as the cell-set restriction: "Ion sent an empty
    // list" resolving to "leave the old highlight up" would leave a ring on a
    // cell the current answer is not about.
    if (highlightCellIds) {
      highlightedCellIds.value =
        highlightCellIds.length > 0 ? new Set(highlightCellIds) : null
    }

    // Two writes that are NOT optional, and both were missing until a review
    // caught them. They are the difference between set_plot working and
    // set_plot destroying the turn that issued it.
    //
    // `activePreset = null`: FreePlotSettingsOverlay mounts UNCONDITIONALLY with
    // the graph view (FreePlotView.jsx:125), and its mount effect re-applies the
    // stored preset whenever plotMode is 'user' — which is the default. That
    // effect calls handlePresetClick, which overwrites all three axes AND calls
    // resetAllFilters(). So the chain was: Ion filters and sets axes → this
    // command switches to graph → the drawer mounts → the preset is re-applied →
    // Ion's axes are replaced by the preset's and Ion's filters (and, since D4,
    // Ion's cell set) are wiped, in the same turn, while the Revert card claims
    // the change was made. Nulling the preset makes the mount effect a no-op,
    // which is exactly what the manual axis dropdowns already do
    // (FreePlotSettingsOverlay.jsx:598) when the user picks an axis by hand.
    //
    // `plotMode = 'expert'`: the Axes card is gated on expert mode (:579), so in
    // Guided mode Ion would set axes the user can neither see nor adjust. This
    // overwrites a persisted preference, deliberately — it is what a person does
    // by hand to reach the same view, it is visible, and REVERT restores it
    // because takeSnapshot now carries both signals.
    activePreset.value = null
    plotMode.value = 'expert'

    setProvenance('plot', source)
  } finally {
    commandDepth--
  }
}

/**
 * Table column visibility (Ion's `set_table_columns`, D2).
 *
 * `tableVisibleColumns` only takes effect while `tableDensity === 'custom'`
 * (TableView.jsx: the standard and detailed densities use their own fixed column
 * sets). Setting the flags without switching the density is therefore a silent
 * no-op — the signal changes, the table does not — so this always switches, and
 * seeds from the CURRENT set so a merge keeps whatever the user could see.
 *
 * @param {object} opts
 * @param {string[]} [opts.show] - columns to make visible
 * @param {string[]} [opts.hide] - columns to hide
 * @param {boolean} [opts.merge=true] - false starts from an empty table, so
 *   `show` becomes the complete set ("show ONLY name and energy")
 * @param {'grid'|'table'|'graph'} [opts.view] - always 'table' from the backend
 * @param {'user'|'ion'} [opts.source='ion']
 */
export function applyTableColumnsCommand({
  show,
  hide,
  merge = true,
  view,
  source = 'ion',
} = {}) {
  commandDepth++
  try {
    // Layout first, same reasoning as applyPlotCommand: the table the columns
    // belong to should be mounted before they change.
    if (view) applyViewCommand({ view, source })

    // The density the user was on decides what "merge" means. On 'standard' or
    // 'detailed' the visible set comes from the density, NOT from
    // tableVisibleColumns — so seed from what was actually on screen, not from a
    // signal the user was not looking at. Getting this wrong would make "also
    // show post-formation loss" from the detailed density silently DROP
    // chemistry, which the user could see a moment ago.
    const effective = columnsForDensity(tableDensity.peek(), tableVisibleColumns.peek())
    const next = merge ? { ...effective } : Object.fromEntries(
      Object.keys(effective).map((k) => [k, false]),
    )
    for (const key of show || []) next[key] = true
    for (const key of hide || []) next[key] = false

    tableVisibleColumns.value = next
    // Without this the flags above are inert. Set AFTER computing `effective`,
    // which reads the density.
    tableDensity.value = 'custom'

    setProvenance('columns', source)
  } finally {
    commandDepth--
  }
}

/**
 * Apply a context change: basis (abs/grav/vol) + domain.
 *
 * @param {object} opts
 * @param {'abs'|'grav'|'vol'} [opts.basis]
 * @param {'energy-power'|'capacity-current'} [opts.domain]
 * @param {'user'|'ion'} [opts.source='ion']
 */
export function applyContextCommand({ basis: newBasis, domain: newDomain, source = 'ion' } = {}) {
  commandDepth++
  try {
    // A real basis/domain change is what FilterPanel.jsx's
    // `useEffect(..., [basis.value, domain.value])` reacts to — it fires an
    // ASYNC metadata reload that lands well after this function returns
    // (commandDepth back to 0) and writes every RANGE_SIGNAL straight. Arm
    // the guard so the degradation effect below doesn't mistake that reload's
    // writes for a manual change (hotfix: "self-degradation via async basis
    // reload" — see state/ionReloadGuard.js). Only arm it for an ACTUAL value
    // change — a same-value write doesn't trigger FilterPanel's effect deps,
    // so no reload follows and there's nothing to guard against.
    if (newBasis && newBasis !== basis.peek()) markIonBasisReloadExpected()
    if (newDomain && newDomain !== domain.peek()) markIonBasisReloadExpected()

    if (newBasis) basis.value = newBasis
    if (newDomain) domain.value = newDomain

    setProvenance('context', source)
  } finally {
    commandDepth--
  }
}

// ============================================================================
// PROVENANCE DEGRADATION (Task 10)
// ============================================================================
//
// The attribution card (ActiveFiltersHeader.jsx) only offers Revert while
// commandProvenance names a committed Ion turn. The moment the user makes a
// manual filter/sort/view/context change — FilterPanel's facet chips, the
// sort dropdown, the view toggles, and ActiveFiltersHeader's own "×"/"Clear
// All" all still call toggleFilter()/updateRange()/resetAllFilters() etc.
// straight, bypassing this layer (full click-handler redirection is out of
// scope for Task 10) — the snapshot a Revert would restore is stale, so
// provenance must degrade to null and the card must disappear.
//
// This effect watches every signal a manual change can hit and nulls
// commandProvenance whenever one of them changes while commandDepth is 0,
// i.e. the write did NOT come from this file.
//
// Split in two: FACET_SIGNALS/view/sort/search/basis/domain are only ever
// written synchronously (by this file, guarded by commandDepth, or by a
// manual click) — one effect, no extra guard needed. RANGE_SIGNALS are
// DIFFERENT: a basis/domain change also gets written asynchronously later by
// filterStore.js's initFilterRangesFromMetadata() (FilterPanel.jsx's
// [basis,domain] metadata reload), long after commandDepth is back to 0 —
// that write needs the ionReloadGuard (see markIonBasisReloadExpected()
// above) or it reads as a manual change and self-degrades the very
// attribution the Ion command that triggered the reload just committed.
effect(() => {
  // Touch every signal a manual facet/view/sort/search/context change can
  // reach, so any write to them re-runs this effect.
  for (const sig of Object.values(FACET_SIGNALS)) void sig.value
  void currentView.value
  void searchQuery.value
  void sortBy.value
  void sortOrder.value
  // Both sort pairs — a manual table-header/dropdown sort is as much a user
  // override as a grid sort, so it must degrade Ion's attribution too.
  void tableSortBy.value
  void tableSortOrder.value
  void basis.value
  void domain.value
  // The free-plot axes and colour-by: a user who hand-picks an axis after an Ion
  // turn is overriding it exactly as much as one who unticks a facet, so it has
  // to degrade the attribution too. Without these three the Revert card stayed
  // up pointing at a snapshot whose axes the user had already replaced.
  //
  // plotMode / activePreset are deliberately NOT touched here, even though
  // applyPlotCommand writes them: plotPresets.js's URL effect and the settings
  // drawer's mount effect also write them at commandDepth === 0, so watching
  // them would degrade Ion's attribution for reasons the user never caused.
  void freePlotXAxis.value
  void freePlotYAxis.value
  void freePlotColorBy.value

  // .peek() so reading commandProvenance here doesn't make IT a dependency
  // of this effect too (that would refire this effect on every commit/revert
  // for no reason, including the write this effect itself performs below).
  if (commandDepth === 0 && commandProvenance.peek()) {
    commandProvenance.value = null
    // The committed snapshot's card is going away with the provenance it was
    // attached to — a manual change means Revert no longer means anything
    // (blocker fix: see committedSnapshot below).
    committedSnapshot = null
  }
})

effect(() => {
  for (const sig of Object.values(RANGE_SIGNALS)) void sig.value

  if (commandDepth === 0 && !isIonBasisReloadExpected() && commandProvenance.peek()) {
    commandProvenance.value = null
    committedSnapshot = null
  }
})

// ============================================================================
// TRANSACTIONS (REVERT-SNAPSHOT)
// ============================================================================
//
// Two module-level slots, not a stack — beginIonTransaction() is called
// exactly ONCE per Ion turn (guarded by useChatStream.js's
// ionTransactionStartedFor); the apply*Command functions never push their
// own snapshot, they only track commandDepth. So there is never more than
// one "in-flight" and one "committed" snapshot to reason about:
//
//   openSnapshot      — the pre-turn state of the turn currently in
//                        progress (set by beginIonTransaction, cleared by
//                        commitIonTransaction/rollbackOpenTransaction).
//   committedSnapshot — the pre-turn state of the LAST turn that committed,
//                        i.e. what the attribution card's Revert button
//                        restores to. Cleared once consumed (revert) or once
//                        stale (a manual change degrades commandProvenance).
//
// (Blocker fix: the previous LIFO transactionStack discarded a turn's
// snapshot the instant it committed — commitIonTransaction() popped it
// without storing it anywhere — so clicking Revert on the just-shown
// attribution card, which only ever appears AFTER commit, always popped an
// empty stack and did nothing. committedSnapshot is what the card's Revert
// actually reads.)
//
// Guided Tour Redesign, Part D2: the capability gallery's playRecordedFlow()
// (hooks/useChatStream.js) goes through this exact same
// beginIonTransaction/commitIonTransaction path — a replay IS a turn from
// here's point of view, so it gets the identical "Ion: … · Revert" card,
// same wording, no separate code path. But because these are two SLOTS and
// not a stack, playing two recordings back to back means the second commit
// OVERWRITES committedSnapshot with the first recording's post-state — one
// Revert click undoes only the second replay, landing wherever the first
// one left things, not back to where the user started before either replay
// ran. Confirmed by
// tests/explorer-commands/capability-gallery-revert.test.js (and, for live
// turns, tests/explorer-commands/abort-transaction-cleanup.test.js). This is
// a known, reported limitation — not something D2 is authorized to fix by
// introducing a stack; that's a product decision for BA.
let openSnapshot = null
let committedSnapshot = null

function takeSnapshot() {
  const facets = {}
  for (const [type, sig] of Object.entries(FACET_SIGNALS)) {
    facets[type] = [...sig.value]
  }

  const ranges = {}
  for (const [type, sig] of Object.entries(RANGE_SIGNALS)) {
    ranges[type] = { ...sig.value }
  }

  return {
    appViewMode: appViewMode.value,
    currentView: currentView.value,
    urlView: new URLSearchParams(window.location.search).get('view'),
    searchQuery: searchQuery.value,
    facets,
    ranges,
    basis: basis.value,
    domain: domain.value,
    sortBy: sortBy.value,
    sortOrder: sortOrder.value,
    tableSortBy: tableSortBy.value,
    tableSortOrder: tableSortOrder.value,
    // M1: a real Ion turn never toggles the sidebar, so this is harmless for
    // the transaction revert/commit path — it only matters for ion-intro's
    // demo (tourDefinitions.js `intro-drive` forces it open), where NOT
    // restoring it left the sidebar open after the baseline restore.
    sidebarVisible: sidebarVisible.value,
    // Free-plot axes + colour-by. Added with `set_plot` (C2): before Ion could
    // set these, nothing in a transaction touched them, so leaving them out of
    // the snapshot cost nothing. Now it would break the one promise the
    // transaction makes — REVERT would put the filters and sort back and leave
    // the user staring at Ion's axes, with no way to undo them.
    freePlotXAxis: freePlotXAxis.value,
    freePlotYAxis: freePlotYAxis.value,
    freePlotColorBy: freePlotColorBy.value,
    // Table columns. Added with `set_table_columns` (D2), for exactly the reason
    // above — and the density MUST come along with the flags: the command forces
    // 'custom' to make the flags take effect at all, so restoring the flags
    // without the density would leave the user in custom mode with their old
    // selection, which is a different table from the one they started with.
    tableDensity: tableDensity.value,
    tableVisibleColumns: { ...tableVisibleColumns.value },
    // Ion's id set (D4). Copied, not referenced — a Set handed straight into the
    // snapshot would be the SAME object the command then mutates past, so a
    // revert would restore the post-command state and look like it did nothing.
    ionCellIdSet: ionCellIdSet.value ? new Set(ionCellIdSet.value) : null,
    ionCellIdSetLabel: ionCellIdSetLabel.value,
    // Both are WRITTEN by applyPlotCommand (see there), so both have to come
    // back on Revert — restoring the axes while leaving the user in expert mode
    // with their Guided preset gone is not the state they started in.
    plotMode: plotMode.value,
    activePreset: activePreset.value,
    // Copied, not referenced — the same trap the cell-set restriction has: a Set
    // handed straight in would be the object the command then mutates past, and
    // the revert would restore the post-command state.
    highlightedCellIds: highlightedCellIds.value
      ? new Set(highlightedCellIds.value)
      : null,
  }
}

function restoreSnapshot(snapshot) {
  commandDepth++
  try {
    appViewMode.value = snapshot.appViewMode
    currentView.value = snapshot.currentView
    // Merge into the existing query (I2) instead of replacing it wholesale.
    navigate(mergeViewIntoQuery(snapshot.urlView), { replace: true })

    searchQuery.value = snapshot.searchQuery

    for (const [type, sig] of Object.entries(FACET_SIGNALS)) {
      sig.value = [...snapshot.facets[type]]
    }

    domain.value = snapshot.domain
    sortBy.value = snapshot.sortBy
    sortOrder.value = snapshot.sortOrder
    // Older snapshots (pre-§10.2) won't carry the table pair — guard so revert
    // never writes `undefined` into the table sort.
    if (snapshot.tableSortBy !== undefined) tableSortBy.value = snapshot.tableSortBy
    if (snapshot.tableSortOrder !== undefined) tableSortOrder.value = snapshot.tableSortOrder
    // Older snapshots (pre-M1) won't carry sidebarVisible — same guard.
    if (snapshot.sidebarVisible !== undefined) sidebarVisible.value = snapshot.sidebarVisible
    // Same guard for the free-plot trio (pre-C2 snapshots): a persisted
    // transaction taken before set_plot existed must not write `undefined`
    // into the axes and blank the chart on revert.
    if (snapshot.freePlotXAxis !== undefined) freePlotXAxis.value = snapshot.freePlotXAxis
    if (snapshot.freePlotYAxis !== undefined) freePlotYAxis.value = snapshot.freePlotYAxis
    if (snapshot.freePlotColorBy !== undefined) freePlotColorBy.value = snapshot.freePlotColorBy
    // Same guard, same reason: a snapshot from before set_table_columns has
    // neither key, and writing `undefined` would blank every column.
    if (snapshot.tableDensity !== undefined) tableDensity.value = snapshot.tableDensity
    if (snapshot.tableVisibleColumns !== undefined) {
      tableVisibleColumns.value = { ...snapshot.tableVisibleColumns }
    }
    // `undefined` = pre-D4 snapshot (leave alone); `null` = there was no
    // restriction, which must be restored, so the check is against undefined
    // only. Getting this wrong the other way would make a revert unable to
    // REMOVE a set Ion had added — the most likely thing a user wants undone.
    // `!== undefined`, not truthiness: `null` means "nothing was marked" and has
    // to be restorable, or Revert could never REMOVE a highlight Ion added.
    if (snapshot.highlightedCellIds !== undefined) {
      highlightedCellIds.value = snapshot.highlightedCellIds
        ? new Set(snapshot.highlightedCellIds)
        : null
    }
    if (snapshot.plotMode !== undefined) plotMode.value = snapshot.plotMode
    // `!== undefined`, not truthiness: `null` is the meaningful "no preset was
    // active" value and must be restorable.
    if (snapshot.activePreset !== undefined) activePreset.value = snapshot.activePreset
    if (snapshot.ionCellIdSet !== undefined) {
      ionCellIdSet.value = snapshot.ionCellIdSet ? new Set(snapshot.ionCellIdSet) : null
      ionCellIdSetLabel.value = snapshot.ionCellIdSetLabel || ''
    }

    const basisChanged = basis.value !== snapshot.basis
    if (basisChanged) {
      basis.value = snapshot.basis
      // Same reasoning as applyFilterCommand: a basis change triggers the async
      // metadata reload, so route the restored ranges through
      // pendingRangeOverrides instead of writing the range signals directly.
      const overrides = { ...(pendingRangeOverrides.value || {}) }
      for (const [type, range] of Object.entries(snapshot.ranges)) {
        overrides[`${type}Min`] = range.min
        overrides[`${type}Max`] = range.max
      }
      pendingRangeOverrides.value = overrides
    } else {
      for (const [type, range] of Object.entries(snapshot.ranges)) {
        RANGE_SIGNALS[type].value = { ...range }
      }
    }

    setProvenance('revert', 'ion')
  } finally {
    commandDepth--
  }
}

/**
 * Start an Ion transaction: snapshots all explorer state that commands in
 * this layer can touch, so the turn can be committed (keeping the changes,
 * card-revertible) or rolled back (mid-turn failure) later.
 * @returns {object} the snapshot just taken
 */
export function beginIonTransaction() {
  // The dispatcher (ionTransactionStartedFor, useChatStream.js) guarantees
  // this is called at most once per open turn — but stay defensive rather
  // than clobbering an in-flight snapshot if it somehow is called again.
  if (openSnapshot) return openSnapshot
  openSnapshot = takeSnapshot()
  return openSnapshot
}

/**
 * Commit the open transaction — keeps the state changes made since
 * beginIonTransaction(), and promotes its pre-turn snapshot to
 * `committedSnapshot` so the attribution card's Revert button (clicked well
 * after this call returns) has something to restore to.
 * @param {string} [label] - human-readable label for the committed turn
 */
export function commitIonTransaction(label) {
  committedSnapshot = openSnapshot
  openSnapshot = null
  setProvenance('commit', 'ion', { label })
}

/**
 * Roll back the open (NOT YET committed) transaction — used when a turn
 * fails mid-stream (the `error` event) and may have applied some but not
 * all of its commands. Restores state to what it was at the matching
 * beginIonTransaction() call.
 */
export function rollbackOpenTransaction() {
  if (!openSnapshot) return
  const snapshot = openSnapshot
  openSnapshot = null
  restoreSnapshot(snapshot)
}

/**
 * Revert a COMMITTED transaction — what the attribution card's Revert
 * button calls. Restores state to what it was right before the last
 * committed turn ran, then clears `committedSnapshot` (one-shot: reverting
 * twice in a row without a new commit in between is a no-op).
 */
export function revertIonTransaction() {
  if (!committedSnapshot) return
  const snapshot = committedSnapshot
  committedSnapshot = null
  restoreSnapshot(snapshot)
}

// ============================================================================
// STANDALONE SNAPSHOT/RESTORE (non-Ion-turn callers)
// ============================================================================
//
// Thin wrappers around takeSnapshot/restoreSnapshot for callers that need to
// snapshot-and-later-restore Explorer state WITHOUT participating in the
// begin/commit/revert transaction above — e.g. the ion-intro onboarding tour
// (tourDefinitions.js), whose scripted demo drives applyFilterCommand/
// applyViewCommand directly rather than through a live Ion turn. Reusing
// beginIonTransaction/commitIonTransaction here would either clobber a real
// turn's in-flight snapshot or leave the demo's snapshot sitting in
// `committedSnapshot`, making the attribution card's Revert button restore
// to the demo's pre-state instead of the real turn's.

/** Snapshot all Explorer state this layer can touch. */
export function snapshotExplorerState() {
  return takeSnapshot()
}

/** Restore Explorer state from a snapshot taken by snapshotExplorerState(). */
export function restoreExplorerState(snapshot) {
  restoreSnapshot(snapshot)
}
